import { Api } from "../util/Api";
import { getDB } from "../util/db";
import { isoTimestamp, positionSort } from "../util/utils";

export class Survey {

	get displayName() {
		if(this.deletedAt == null) {
			return this.name;
		}
		return `${this.name} (Deprecated)`;
	}

	static hydrate(surveyData) {
		let surveyObj = new Survey();
		Object.assign(surveyObj, surveyData);
		return surveyObj;
	}

	static async find(surveyId) {	
		let db = await getDB();
		let survey = await db.get("Surveys", surveyId);
		return survey;
	}

	// NOTE - survey updates are actually re-creations, so that we have consistent historical data.
	//        
	static async adminUpdateSurvey(survey, user) {
		let db = await getDB();

		// This will always yield a new survey
		let savedSurvey = await Api.forProfile(user).inspectionCall("/v2/surveys", "POST", survey);
		await db.put("Surveys", savedSurvey);

		// Mark the old survey as deleted
		if(survey.id != null) {
			// NOTE - need to reload old survey or weird stuff happens,
			// because "survey" is a mix of old and new at this point
			let oldSurvey = await db.get("Surveys", survey.id);
			oldSurvey.deletedAt = isoTimestamp();
			await db.put("Surveys", oldSurvey);
		}

		// Return the new survey
		return savedSurvey;
	}

	static async adminDuplicateSurvey(survey, user) {
		let newSurvey = Object.assign({}, survey); // Just a shallow assign but should work fine for this purpose

		newSurvey.id = null;
		newSurvey.name = `${survey.name} Copy`;
		newSurvey.global = false; // All surveys start as local
		return Survey.adminUpdateSurvey(newSurvey, user);
	}

	
	static async adminDeleteSurvey(survey, user) {
		await Api.forProfile(user).inspectionCall(`/v2/surveys/${survey.id}`, "DELETE");
		survey.deletedAt = isoTimestamp();
		let db = await getDB();
		await db.put("Surveys", survey);
	}

	// Tells us if this survey contains a component with the given id
	containsComponentTypeId(cid){
		for(let sct of this?.surveyComponentTypes){
			if(sct?.componentTypeId == cid){
				return true;
			}
		}
		return false;
	}

	static async getActiveSurveyForComponentTypeId(cid, surveyMeta) {

		// Filter for only surveys matching our component type
		let activeSurveys = await Survey.getActiveSurveys();
		let surveys = activeSurveys.filter(s => s.containsComponentTypeId(cid));
		// Try to get a more exact match based on previous survey
		if(surveyMeta != null) {
			// Prefer exact matches
			let tmpSurveys = surveys.filter(s => s.id == surveyMeta.id);
			if(tmpSurveys.length == 0) {
				// No exact match---attempt name match
				tmpSurveys = surveys.filter(s => s.name == surveyMeta.name);
			}

			// Only overwrite survey list if we found something
			if(tmpSurveys.length > 0) {
				surveys = tmpSurveys;
			}
		}

		// Return the best one found
		return surveys[0];
	}
	
	static async getActiveSurveys(extraId) {
		let db = await getDB();
		let surveys = await db.getAll("Surveys");
	
		let filteredSurveys = surveys.filter(survey => (survey.deletedAt == null || survey.id == extraId)).sort(positionSort);
		return filteredSurveys.map(s => Survey.hydrate(s));
	}
	
	static async syncSurveys(user) {
		try {
			let surveys = await Api.forProfile(user).inspectionCall("/v2/surveys/active");
			let db = await getDB();
			let tx = db.transaction("Surveys", "readwrite");
			let store = tx.store;
	
			// Set deletion times on all surveys
			let ts = isoTimestamp();
			let storedSurveys = await store.getAll();
			for(const survey of storedSurveys) {
				survey.deletedAt = ts;
				await store.put(survey);
			};
	
			// Save new surveys
			for(const survey of surveys) {
				await store.put(survey);
			};
	
			await tx.done;
			console.log("Survey data loaded");
		} catch(err) {
			console.log("Error syncing surveys");
			console.log(err);
		}
	}

	// NOTE - in the future, we can also apply any forced orderings here
	static groupSurveysByCategory(surveys) {
		// Failsafe
		if(surveys == null) {
			return null;
		}

		// Create groups
		let groups = {};
		let cats = [];
		let idx = 0;
		for(const surveyIdx in surveys) {
			let survey = surveys[surveyIdx];
			let catName = survey.categoryName || "";
			groups[catName] ||= {
				name: catName,
				position: (catName == "" ? -1 : idx), // empty name always gets first position
				options: []
			};
			groups[catName].options.push(survey);
			idx += 1;
		}

		// Sort groups
		return Object.values(groups).sort((a, b) => (a.position - b.position))
	}

	static async ensureSurveysPresent(profile, surveyIds) {
		let db = await getDB();
		for(const surveyId of surveyIds) {
			if(!!surveyId && surveyId != "undefined") { // Not sure why some come through with the string "undefined" (should be null instead, or actually undefined)
				let survey = await db.get("Surveys", surveyId);
				if(survey == null) {
					let tmpSurvey = await Api.forProfile(profile).inspectionCall(`/v2/surveys/${encodeURIComponent(surveyId)}`);;
					await db.put("Surveys", tmpSurvey);	
				}	
			}
		};
	}
}